/* eslint-disable no-unused-vars */

/* This component is under redesign. To make redesign smother it's currently still has
previous version. To avoid lint issues we are disabling eslint for this block
 moved wrapper from pages/Routes/IndexPage.js to keep all code in one place and added additional changes to the code */

import React, { useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { getRoutes, createRoute } from '../../utils/api/routes';
import { formatLongDate, isDateInRange } from '../../utils/dates';
import { withContextProvider } from '../../hoc';
import { DialogsProvider, ToastProvider } from '../../providers';
import { useToasts } from '../../hooks';
import { handleResponse } from '../../utils/api/errorHandler';
import RoutesPage from './RoutesPage';
import RouteAddEditModal from '../../componentsV2/routes/Modal/RouteAddEditModal';
import { updateSearchParamsAndUrl } from '../../utils/updateSearchParamsAndUrl';
import useSyncedSearchParams from '../../hooks/useSyncedSearchParams';
import { parseParams } from '../../utils/parseParams';
import { useUrlModals } from '../../utils/useUrlModals';
import { redirectToRouteEdit } from '../../utils/navigation';
import { useLocalStorage } from '../../hooks/useLocalStorage';

const parseRouteParams = (searchParams) => parseParams(searchParams, {
  routeKeyword: {},
  routeName: {},
  routesStartDate: {},
  routesEndDate: {},
  sortBy: { default: 'date' },
  direction: { default: 'desc' },
  limit: { type: 'int', default: 20 },
  offset: { type: 'int', default: 0 },
});

const getRoutePage = (searchParams) => parseInt(searchParams.get('page'), 10) || 1;

const RoutesPageWrapper = ({ currentRoutes = [], organizationId }) => {
  // Hook Toast
  const { showToast } = useToasts();
  const { getFromLocalStorage, removeFromLocalStorage } = useLocalStorage();

  const {
    params, setParams, currentPage, setCurrentPage,
  } = useSyncedSearchParams(
    parseRouteParams,
    getRoutePage,
  );

  const itemsPerPage = 20;
  const offset = (currentPage - 1) * itemsPerPage;

  const [totalRoutes, setTotalRoutes] = useState(0);
  const [totalUnfilteredRoutes, setTotalUnfilteredRoutes] = useState(0);
  const [filteredRoutes, setFilteredRoutes] = useState(currentRoutes);
  const [loading, setLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // useUrlModals for Add Route modal
  const { modals, openModal, closeModal } = useUrlModals({
    addRoute: { isIdParam: false },
  });

  const onSort = ({ field, order }) => {
    const updatedParams = {
      ...params, sortBy: field, direction: order, offset: 0, page: 1,
    };
    setParams(updatedParams);
    setCurrentPage(1);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  const onPageChange = (page) => {
    const updatedParams = { ...params, offset: (page - 1) * itemsPerPage, page };
    setParams(updatedParams);
    setCurrentPage(page);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  const onSearch = (values = {}, isReset = false) => {
    const {
      keyword: routeKeywordInput, name: routeNameInput, dateFrom, dateTo,
    } = values;
    const routesStartDate = dateFrom ? formatLongDate(dateFrom) : '';
    const routesEndDate = dateTo ? formatLongDate(dateTo) : '';

    // Only validate date range if both startDate and endDate values are provided
    if (routesStartDate && routesEndDate && !isDateInRange(dateFrom, dateTo)) {
      showToast({
        message: 'Please enter a valid date range. Start date must fall before end date',
        type: 'error',
      });
      return;
    }

    const updatedParams = {
      ...params,
      routeKeyword: routeKeywordInput,
      routeName: routeNameInput,
      routesStartDate,
      routesEndDate,
      offset: 0,
      page: 1,
    };

    setParams(updatedParams);
    setCurrentPage(1);
    updateSearchParamsAndUrl({ params: updatedParams });
  };

  useEffect(() => {
    const {
      routesStartDate, routesEndDate, routeKeyword, routeName,
    } = params;

    // Only validate date range if both startDate and endDate values are provided
    if (routesStartDate && routesEndDate && !isDateInRange(routesStartDate, routesEndDate)) {
      showToast({
        message: 'Please enter a valid date range. Start date must fall before end date',
        type: 'error',
      });
      return;
    }

    setLoading(true);

    getRoutes(organizationId, params)
      .then(handleResponse)
      .then((data) => {
        setFilteredRoutes(data.routes);

        // set totalRoutes based on whether search parameters are active
        const isSearchActive = !!routeKeyword || !!routeName || !!routesStartDate || !!routesEndDate;
        setTotalRoutes(isSearchActive ? data.result_count : data.total_count);
        setTotalUnfilteredRoutes(data.total_count);
      })
      .catch((error) => showToast({ message: error.message, type: 'error' }))
      .finally(() => setLoading(false));
  }, [params, showToast, organizationId]);

  // show success toast message after redirection during successful route deletion
  useEffect(() => {
    const toastMessage = getFromLocalStorage('routeDeletedMessage');
    removeFromLocalStorage('routeDeletedMessage');
    if (toastMessage) {
      showToast({ message: toastMessage, type: 'success' });
    }
  }, [getFromLocalStorage, removeFromLocalStorage, showToast]);

  const handleCreateRoute = (data) => {
    setIsSaving(true);
    createRoute(data, organizationId)
      .then(handleResponse)
      .then((newRoute) => {
        closeModal('addRoute');
        onSearch({}, true);
        showToast({ message: 'Route created!', type: 'success' });
        const routeId = newRoute?.route?.id;
        if (routeId) {
          setTimeout(() => redirectToRouteEdit(routeId), 50);
        }
      })
      .catch((error) => showToast({ message: error.message, type: 'error' }))
      .finally(() => setIsSaving(false));
  };

  return (
    <DialogsProvider>
      <RoutesPage
        routes={filteredRoutes}
        totalRoutes={totalRoutes}
        totalUnfilteredRoutes={totalUnfilteredRoutes}
        onSearch={(values) => onSearch(values, true)}
        onSort={onSort}
        direction={params.direction}
        organizationId={organizationId}
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
        setCurrentPage={onPageChange}
        handleOpenAddRouteModal={() => openModal('addRoute')}
        activeSearchParams={params}
      />
      {modals.addRoute?.isOpen && (
        <RouteAddEditModal
          onCloseModal={() => closeModal('addRoute')}
          title="Create Route"
          onSubmit={handleCreateRoute}
          isSaving={isSaving}
        />
      )}
    </DialogsProvider>
  );
};

RoutesPageWrapper.propTypes = {
  currentRoutes: PropTypes.array,
  organizationId: PropTypes.number.isRequired,
};

export default withContextProvider(ToastProvider)(RoutesPageWrapper);
